import { MemberService } from "@/gen/omnios/v1/member_connect.js";
import type {
	WorkspaceInvite,
	WorkspaceInviteLink,
	WorkspaceMember,
} from "@/gen/omnios/v1/member_pb.js";
import { createClient } from "@connectrpc/connect";
import { create } from "zustand";
import { transport } from "./grpc";

export type MemberRole = "owner" | "admin" | "member" | "viewer";

export interface Member {
	id: string;
	name: string;
	email: string;
	role: MemberRole;
	avatarUrl: string;
	joinedAt: string;
}

export interface PendingInvite {
	id: string;
	email: string;
	role: MemberRole;
	sentAt: string;
}

export interface InviteLink {
	id: string;
	token: string;
	role: MemberRole;
	maxUses: number | null;
	useCount: number;
	createdAt: string;
	expiresAt: string;
}

function pbMemberToLocal(m: WorkspaceMember): Member {
	return {
		id: m.id,
		name: m.name,
		email: m.email,
		role: m.role as MemberRole,
		avatarUrl: m.avatarUrl ?? "",
		joinedAt: m.joinedAt
			? new Date(Number(m.joinedAt.seconds) * 1000).toISOString()
			: "",
	};
}

function pbInviteToLocal(i: WorkspaceInvite): PendingInvite {
	return {
		id: i.id,
		email: i.email,
		role: i.role as MemberRole,
		sentAt: i.createdAt
			? new Date(Number(i.createdAt.seconds) * 1000).toISOString()
			: "",
	};
}

function pbLinkToLocal(l: WorkspaceInviteLink): InviteLink {
	return {
		id: l.id,
		token: l.token,
		role: l.role as MemberRole,
		maxUses: l.maxUses ?? null,
		useCount: l.useCount,
		createdAt: l.createdAt
			? new Date(Number(l.createdAt.seconds) * 1000).toISOString()
			: "",
		expiresAt: l.expiresAt
			? new Date(Number(l.expiresAt.seconds) * 1000).toISOString()
			: "",
	};
}

const client = createClient(MemberService, transport);

interface MembersState {
	members: Member[];
	inviteLinks: InviteLink[];
	pendingInvites: PendingInvite[];
	isLoading: boolean;
	error: string | null;
	fetchMembers: (search?: string) => Promise<void>;
	fetchInvites: () => Promise<void>;
	fetchInviteLinks: () => Promise<void>;
	updateMemberRole: (memberId: string, role: MemberRole) => Promise<void>;
	removeMember: (memberId: string) => Promise<void>;
	inviteByEmail: (email: string, role: MemberRole) => Promise<void>;
	createInviteLink: (role: MemberRole) => Promise<InviteLink>;
	revokeInviteLink: (id: string) => Promise<void>;
	cancelInvite: (id: string) => Promise<void>;
}

export const useMembersStore = create<MembersState>()((set, get) => ({
	members: [],
	inviteLinks: [],
	pendingInvites: [],
	isLoading: false,
	error: null,

	fetchMembers: async (search?: string) => {
		set({ isLoading: true, error: null });
		try {
			const resp = await client.listMembers({
				search: search ?? "",
			});
			set({
				members: resp.members.map(pbMemberToLocal),
				isLoading: false,
			});
		} catch (e: unknown) {
			set({
				isLoading: false,
				error: e instanceof Error ? e.message : i18n.t("loading_error"),
			});
		}
	},

	fetchInvites: async () => {
		try {
			const resp = await client.listInvites({});
			set({
				pendingInvites: resp.invites.map(pbInviteToLocal),
			});
		} catch {
			// silently ignore
		}
	},

	fetchInviteLinks: async () => {
		try {
			const resp = await client.listInviteLinks({});
			set({
				inviteLinks: resp.links.map(pbLinkToLocal),
			});
		} catch {
			// silently ignore
		}
	},

	updateMemberRole: async (memberId, role) => {
		const resp = await client.updateMemberRole({
			memberId,
			role,
		});
		const updated = pbMemberToLocal(resp);
		set({
			members: get().members.map((m) => (m.id === memberId ? updated : m)),
		});
	},

	removeMember: async (memberId) => {
		await client.removeMember({ memberId });
		set({
			members: get().members.filter((m) => m.id !== memberId),
		});
	},

	inviteByEmail: async (email, role) => {
		const resp = await client.inviteByEmail({
			email,
			role,
		});
		set({
			pendingInvites: [...get().pendingInvites, pbInviteToLocal(resp)],
		});
	},

	createInviteLink: async (role) => {
		const resp = await client.createInviteLink({ role });
		const link = pbLinkToLocal(resp);
		set({
			inviteLinks: [...get().inviteLinks, link],
		});
		return link;
	},

	revokeInviteLink: async (id) => {
		await client.revokeInviteLink({
			linkId: id,
		});
		set({
			inviteLinks: get().inviteLinks.filter((l) => l.id !== id),
		});
	},

	cancelInvite: async (id) => {
		await client.cancelInvite({
			inviteId: id,
		});
		set({
			pendingInvites: get().pendingInvites.filter((i) => i.id !== id),
		});
	},
}));

import i18n from "i18next";

export function getRoleLabels(): Record<MemberRole, string> {
	return {
		owner: i18n.t("common:role_owner"),
		admin: i18n.t("common:role_admin"),
		member: i18n.t("common:role_member"),
		viewer: i18n.t("common:role_viewer"),
	};
}
